'use client';

import { CbBorderRadius100 } from '@coolblue-development/design-tokens';
import { Box, freddyTheme } from '@coolblue-development/freddy';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { CookiePreferences } from '@coolblue-development/next-cookies/types';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import styled from '@emotion/styled';
import { ReactNode } from 'react';

export type RequestContextInfoProps = {
  sessionId: string;
  accountId?: string;
  cookies: CookiePreferences;
};

const RequestContextInfo = ({ sessionId, accountId, cookies }: RequestContextInfoProps) => {
  const { subsidiary, environment, language, isInternalUser, device } = useRequestContext();

  const setCookies = Object.entries(cookies)
    .filter(([, value]) => !!value)
    .map(([key]) => key)
    .join(', ');

  // Get specific client observability
  useClientLogger().info('Request context loaded');

  return (
    <Box
      component='div'
      sx={{
        fontSize: freddyTheme.typography.fontSize.lg,
        lineHeight: 1.75,
        color: freddyTheme.color.font.base,
        backgroundColor: freddyTheme.color.background.secondary,
        borderRadius: CbBorderRadius100,
        border: 'none',
        padding: freddyTheme.spacing[500],
        whiteSpace: 'pre-wrap'
      }}
    >
      <div>
        Environment: <CBSamp>{environment}</CBSamp>
      </div>
      <div>
        Subsidiary: <CBSamp>{subsidiary}</CBSamp>
      </div>
      <div>
        Language: <CBSamp>{language}</CBSamp>
      </div>
      <div>
        Device: <CBSamp>{device}</CBSamp>
      </div>
      <div>
        Session ID: <CBSamp>{sessionId}</CBSamp>
      </div>
      <div>
        Account ID: <CBSamp>{accountId || '-'}</CBSamp>
      </div>
      <div>
        Cookies: <CBSamp>{setCookies || '-'}</CBSamp>
      </div>
      <div>
        Is internal user: <CBSamp>{isInternalUser ? 'true' : 'false'}</CBSamp>
      </div>
    </Box>
  );
};

const CBSamp = ({ children }: { children: ReactNode }) => {
  const Styled = styled.samp`
    color: ${freddyTheme.color.font.accented};
  `;

  return <Styled>{children}</Styled>;
};

export default RequestContextInfo;
